<script>
  /**
   * Entry selector using the full-screen drawer picker
   * Replaces the old EntrySelector dropdown with a more visual browsing experience
   */
  import { lazy } from '#lib/lazy.svelte.js';
  const entry_picker_drawer = lazy(() => import('#lib/EntryPickerDrawer.svelte'));
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import t from '#lib/i18n.svelte.js';
  import { page } from '$app/state';

  let {
    /** @type {string} Entry type filter (e.g., 'image', 'page') */
    entry_type = '',
    /** @type {string[]} Entry IDs to exclude from results */
    excluded_ids = [],
    /** @type {(entry_id: string, type: string, entry: object) => void} Called per entry when selected (use for single-select or when you need per-entry handling) */
    onentryselect,
    /** @type {(entry_ids: string[]) => void} Called once with all selected entry IDs (use for multi-select batch operations) */
    onconfirm,
    /** @type {() => void} Called when selection cleared */
    onentrydeselect,
    /** @type {string} Button label text */
    label = '',
    /** @type {string} Button icon */
    icon = 'search',
    /** @type {string} Button variant for ButtonLabel */
    variant = 'brand subtle-outlined',
    /** @type {string} Confirm button label for single selection */
    confirm_label = '',
    /** @type {string} Confirm button label for multiple selection */
    confirm_label_plural = '',
    /** @type {boolean} Allow multiple selection */
    multi_select = false,
  } = $props();

  let picker_open = $state(false);

  /**
   * Handle entry selection from picker
   * @param {string[]} selected_ids
   */
  function handle_confirm(selected_ids) {
    if (selected_ids.length > 0) {
      // If onconfirm is provided, call it with all IDs at once (preferred for multi-select)
      if (onconfirm) {
        onconfirm(selected_ids);
      } else if (onentryselect) {
        // Fallback: call onentryselect for each entry
        for (const entry_id of selected_ids) {
          onentryselect(entry_id, entry_type, { entry_id });
        }
      }
    }
    picker_open = false;
  }

  function handle_cancel() {
    picker_open = false;
  }

  // Default labels based on context
  let default_label = $derived(label || t('select_entry'));
  let default_confirm_label = $derived(confirm_label || t('select_entry'));
  let default_confirm_label_plural = $derived(confirm_label_plural || t('select_entries', { count: '{{count}}' }));
</script>

<button onclick={() => { picker_open = true; }}>
  <ButtonLabel {variant} start_icon={icon}>
    {default_label}
  </ButtonLabel>
</button>

{#if picker_open && entry_picker_drawer.component}
  <entry_picker_drawer.component
    {entry_type}
    initial_selected={[]}
    {excluded_ids}
    onconfirm={handle_confirm}
    oncancel={handle_cancel}
    user_role={page.data.current_user?.role || 'admin'}
    initial_view='grid'
    confirm_label={default_confirm_label}
    confirm_label_plural={default_confirm_label_plural}
    {multi_select}
  />
{/if}
